import { isAnalyticsOptedOut } from './analytics-identity.js';
import { isObject, isString } from '../shared/runtime-values.js';

type PreferenceState = { status: 'empty' }
  | { status: 'reading'; result: Promise<void> }
  | { status: 'ready'; grantId: string | null; expiresAt: number };
let preference: PreferenceState = { status: 'empty' };
const PREFERENCE_TTL_MS = 60_000;

export function clearCustomerAnalytics(): void {
  preference = { status: 'empty' };
}

export async function refreshCustomerAnalytics(): Promise<void> {
  if (!('window' in globalThis)) return;
  if (isAnalyticsOptedOut()) { clearCustomerAnalytics(); return; }
  if (preference.status === 'reading') return preference.result;
  if (preference.status === 'ready' && Date.now() < preference.expiresAt) return;
  const result = Promise.resolve().then(async () => {
    try {
      const response = await fetch('/api/account/analytics', { credentials: 'same-origin', cache: 'no-store' });
      const payload: unknown = response.ok ? await response.json() : null;
      if (preference !== reading) return;
      if (payload && isObject(payload) && 'enabled' in payload && (payload.enabled === true || payload.enabled === false)) {
        const grantId = payload.enabled && 'grant_id' in payload && isString(payload.grant_id) && /^[a-f0-9-]{36}$/.test(payload.grant_id)
          ? payload.grant_id : null;
        preference = { status: 'ready', grantId, expiresAt: Date.now() + PREFERENCE_TTL_MS };
      }
    } catch { /* A preference failure leaves account collection off. */ }
    finally { if (preference === reading) preference = { status: 'empty' }; }
  });
  const reading: Extract<PreferenceState, { status: 'reading' }> = { status: 'reading', result };
  preference = reading;
  return result;
}

interface CustomerAnalyticsEnvelope { customer_grant_id?: string; customer_event_id?: string }

export function customerAnalyticsEnvelope(): CustomerAnalyticsEnvelope {
  if (preference.status !== 'ready' || !preference.grantId || isAnalyticsOptedOut()) return {};
  return { customer_grant_id: preference.grantId, customer_event_id: crypto.randomUUID() };
}
